import type { TableProps } from 'antd';
import { Button, Modal, Pagination, Select, Space, Table, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import { postApi } from 'apis';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	EditActionSvg,
	SortBottomIcon,
	SortTopIcon,
	TableNextIcon,
	TablePreviousIcon
} from 'utils/svgs';
import { checkPermission, toastText } from 'utils/utils';
import SearchAndFilter from '../SearchAndFilter';
import styles from './index.module.scss';
import './index.scss';
import ClientModeForm from '../ClientModeForm';
import { CopyOutlined, EyeOutlined } from '@ant-design/icons';
import ValidateAdminPassForm from '../ValidateAdminPassForm';
import HotSpotChangeModal from 'components/Global/HotSpotChangeModal';

interface Company {
	id: string;
	name: string;
}

const CustomTable = ({
	dataSource, handleTableChange, onShowSizeChange, isLoading, search, totalRecords, performSearchHandler, performFilterHandler, filterValue, openDrawerHandler, setDrawerInfoHandler, setEditSelectedDevice, showModal, currentPage, pageSize, isSearchCancel, setSearch, setIsSearchCancel, fetchDevices }: any) => {
	const { data: userData } = useSelector((state: any) => state?.userProfile);
	const [companies, setCompanies] = useState<Company[]>([]);
	const [selectedDeviceId, setSelectedDeviceId] = useState<string[]>([]);
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>('select');
	const [open, setOpen] = useState(false);
	const [confirmLoading, setConfirmLoading] = useState(false);

	const [openClientMode, setOpenClientMode] = useState(false);
	const [openValidateAminPassModal, setOpenValidateAminPassModal] = useState(false);
	const [validatePassData, setValidatePassData] = useState<any>(null);
	const [clientModeData, setClientModeData] = useState<any>(null);

	// For change hotspot
	const [openAminPassModalForChangeWifi, setOpenAminPassModalForChangeWifi] = useState(false);
	const [deviceId, setDeviceId] = useState<any>(null);


	const userPermissions = useSelector(
		(state: any) => state?.userProfile
	);
	const isEditPermission = checkPermission(userPermissions?.data?.permissions, {
		permissionName: 'Device',
		permission: ['edit'],
	});

	const isDeletePermission = checkPermission(userPermissions?.data?.permissions, {
		permissionName: 'Device',
		permission: ['delete'],
	});

	// const isAddPermission = checkPermission(userPermissions?.data?.permissions, {
	// 	permissionName: 'Device',
	// 	permission: ['add'],
	// });

	const editDataHandler = (deviceObject: any) => {
		openDrawerHandler();
		setDrawerInfoHandler('Edit Device');
		setEditSelectedDevice(deviceObject);
	};

	// For checkbox
	const rowSelection: TableProps<any>['rowSelection'] = {
		columnWidth: 32,
		selectedRowKeys: selectedDeviceId,
		onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
			const ids = selectedRowKeys.map(String); // convert to string[] if needed
			setSelectedDeviceId(ids);
		},
	};

	const handlePopUp = () => {
		setOpen(true);
	};

	const handleOk = async () => {
		if (selectedCompanyId !== "select") {
			setConfirmLoading(true);
			await postApi('/devices/assign-company', {
				deviceIds: selectedDeviceId,
				companyId: selectedCompanyId === "select" ? null : selectedCompanyId
			}).then((res: any) => {
				if (res?.data?.responseStatus === 200) {
					toastText(res?.data?.message, 'success');
				}
			}).catch((err: any) => {
				toastText(err?.response?.data?.message, 'error');
			}).finally(() => {
				fetchDevices()
				setConfirmLoading(false);
				setOpen(false);
				setSelectedDeviceId([])
			});
		}
	};

	const handleCancel = () => {
		setSelectedCompanyId('select')
		setOpen(false);
	};

	const handleClientMode = (value: string) => {
		setOpenClientMode(true);
		setClientModeData(value)
	};

	const handleViewMode = (value: string) => {
		setOpenValidateAminPassModal(true);
		setValidatePassData(value)
	};

	const handleCopy = (value: any) => {
		navigator.clipboard.writeText(value).then(() => {
			toastText('Copied to clipboard!', 'success');
		}).catch(() => {
			toastText('Failed to copy', 'error')
		});
	};


	useEffect(() => {
		if (userData && userData.companies.length > 0) {
			setCompanies(userData.companies);
		}
	}, [userData]);
	return (
		<>
			<div className={`${styles['body']} user-table`} >
				<SearchAndFilter
					performSearchHandler={performSearchHandler}
					searchValue={search}
					performFilterHandler={performFilterHandler}
					filterValue={filterValue}
					title={'Device List'}
					openDrawerHandler={openDrawerHandler}
					isSearchCancel={isSearchCancel}
					setSearch={setSearch}
					setIsSearchCancel={setIsSearchCancel}
				/>

				<div>
					<Table
						rowSelection={rowSelection}
						rowKey={(record: any) => record?.id}
						dataSource={dataSource}
						pagination={false}
						loading={isLoading}
						onChange={handleTableChange}
						scroll={{ y: 'calc(100vh - 300px)' }}
					>
						<Column
							title={<p style={{ paddingRight: '10px' }}>Name</p>}
							dataIndex="name"
							key="name"
							className="user-column bg-white"
							sorter
							sortDirections={['ascend', 'descend', 'ascend']}
							render={(value: any) => (value ? value : '-')}
							sortIcon={({ sortOrder }) => {
								const isAscend = sortOrder === 'ascend';
								const isDescend = sortOrder === 'descend';

								return (
									<>
										<SortTopIcon fill={isAscend ? '#949494' : undefined} />
										<SortBottomIcon fill={isDescend ? '#949494' : undefined} />
									</>
								);
							}}
						/>

						<Column
							title="Mac Address"
							dataIndex="macAddress"
							key="macAddress"
							className="bg-white"
							render={(value: any) => (value ? value : '-')}
						/>

						<Column
							title="Change Wifi"
							dataIndex="changeWifi"
							key="changeWifi"
							className="bg-white"
							render={(value: any, record: any) => {
								return <>
									<Button
										type="primary"
										onClick={() => {
											setOpenAminPassModalForChangeWifi(true); 
											setDeviceId(record?.id)}}
									>
										Change Wifi
									</Button>
								</>
							}}
						/>

						<Column
							title="Secrete Key"
							dataIndex="secreteKey"
							key="secreteKey"
							className="bg-white"
							render={(value: string) => {
								return value ? (
									<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
										<span>{value}</span>
										<Tooltip title="Copy to clipboard">
											<Button
												type="text"
												icon={<CopyOutlined />}
												onClick={() => handleCopy(value)}
											/>
										</Tooltip>
									</div>
								) : (
									'-'
								);
							}}
						/>

						<Column
							title="Client Mode"
							dataIndex="clientMode"
							key="clientMode"
							className="bg-white"
							render={(value: any, record: any) => {
								return <>
									<Space>
										<Button
											type="primary"
											onClick={() => handleClientMode(record)}
											disabled={record?.isClientModeOn === true || !record?.companyId}
										>
											Client Mode
										</Button>
										{(userPermissions?.data?.isSuperAdmin || userPermissions?.data?.isAdmin) && <Button
											type="primary"
											onClick={() => handleViewMode(record)}
											disabled={record?.isClientModeOn !== true && record?.companyId}
										>
											<EyeOutlined />
										</Button>}
									</Space>
								</>
							}}
						/>

						{userPermissions?.data?.roleName === "SUPER_ADMIN" &&
							<Column
								title="Company Id"
								dataIndex="companyId"
								key="companyId"
								className="bg-white"
								render={(companyId: string | null, record: any) => {
									return (
										companies?.map((company: Company, key: number) => {
											return (company?.id === companyId ? company?.name : '')
										})
									);
								}}
							/>
						}

						{(isEditPermission || isDeletePermission) && (
							<Column
								title="Action"
								dataIndex="action"
								key="action"
								className="bg-white"
								width={"8%"}
								render={(value, data: any) => {
									return (
										<Space size={20}>
											{(isEditPermission ||  userPermissions?.data?.isSuperAdmin) ?
												(<div
													className="cursor-pointer flex align-center justify-center rounded-action-btn"
													onClick={() =>
														editDataHandler(data)
													}
												>
													<EditActionSvg />
												</div>
												) : (
													<div className=" cursor-not-allowed flex align-center justify-center rounded-action-btn">

														<EditActionSvg />
													</div>
												)}

											{/* {(isDeletePermission && userPermissions?.data?.isSuperAdmin) ? (
											<div
												className="cursor-pointer flex align-center justify-center rounded-action-btn "
												onClick={() =>
													deleteDataHandler(data)
												}
											>
												<DeleteActionSvg />
											</div>
										) : (
											<div className=" cursor-not-allowed flex align-center justify-center rounded-action-btn ">
												<DeleteActionSvg />
											</div>
										)} */}
										</Space>
									);
								}}
							/>
						)}

					</Table>
				</div>

				<div className={styles['pagination__wrapper']}>
					<Pagination
						current={currentPage}
						pageSize={pageSize}
						showSizeChanger
						onChange={onShowSizeChange}
						total={totalRecords}
						itemRender={(page, type, originalElement) => {
							return (
								<div className={styles['pagination']}>
									{type === 'prev' ? (
										<button
											className={`${styles['pagination']} ${styles['pagination__button']}`}
										>
											<TablePreviousIcon />
											<p
												className={
													styles['pagination__previous']
												}
											>
												Previous
											</p>
										</button>
									) : type === 'next' ? (
										<>
											<button
												className={`${styles['pagination']} ${styles['pagination__button']}`}
											>
												<p
													className={
														styles['pagination__next']
													}
												>
													Next
												</p>
												<TableNextIcon />
											</button>
										</>
									) : (
										originalElement
									)}
								</div>
							);
						}}
					/>
					{selectedDeviceId.length > 0 && <div style={{ alignItems: 'center', display: 'flex' }}>
						<Button
							className='btn-blue'
							onClick={handlePopUp}
						>
							Assign Devices
						</Button>
					</div>}
				</div>

				<Modal
					title="Assign Device"
					open={open}
					onOk={handleOk}
					confirmLoading={confirmLoading}
					onCancel={handleCancel}
				>
					<Select
						placeholder="Select Organization"
						className={styles['columns__company-select']}
						value={selectedCompanyId}
						onChange={(id: string) => setSelectedCompanyId(id)}
						style={{ marginTop: '20px' }}
					>
						<Select.Option value="select">Select</Select.Option>
						{companies?.map((company: Company, key: number) => (
							<Select.Option value={company.id} key={key}>
								{company.name}
							</Select.Option>
						))}
					</Select>
				</Modal>

				{/* for client mode */}
				<Modal
					open={openClientMode}
					onCancel={() => setOpenClientMode(false)}
					footer={null}
				>
					<ClientModeForm
						deviceId={clientModeData?.id}
						setOpenClientMode={setOpenClientMode}
					/>
				</Modal>

				{/* for eye mode */}
				<Modal
					open={openValidateAminPassModal}
					onCancel={() => setOpenValidateAminPassModal(false)}
					footer={null}
				>
					<ValidateAdminPassForm
						deviceId={validatePassData?.id}
						setOpenClientMode={setOpenValidateAminPassModal}
					/>
				</Modal>


				{/* for change hotspot */}
				<HotSpotChangeModal
					openAminPassModalForChangeWifi={openAminPassModalForChangeWifi}
					setOpenAminPassModalForChangeWifi={setOpenAminPassModalForChangeWifi}
					deviceId={deviceId}
				/>
			</div>
		</>
	);
};

export default CustomTable;
